/**
Author - Venkatesh
Git - https://bitbucket.org/__brijesh/
**/

import {useCallback} from 'react';
import {createAAConsent, getAAConsentStatus} from 'api';
import {handleApiError, triggerToast} from 'helpers';
import useSkipStep from 'hooks/useSkipStep';
import {useAADispatch, ACTION_TYPES} from '../context/AAContext';
import {VIEW_STATES} from 'constants/AAConstants';
import {isConsentComplete, isConsentFailed} from '../services/consentService';

const useAAActions = (updateStatus, startPolling, stopPolling, openInNewTab) => {
  const dispatch = useAADispatch();
  const {skipStep, isLoading: isSkipLoading} = useSkipStep();

  const fetchCurrentConsentStatus = useCallback(async () => {
    try {
      dispatch({type: ACTION_TYPES.SET_LOADING, payload: true});
      const response = await getAAConsentStatus();
      const statusData = response.data;

      if (statusData && statusData.id) {
        dispatch({type: ACTION_TYPES.SET_HAS_EXISTING_CONSENT, payload: true});
        dispatch({
          type: ACTION_TYPES.SET_CONSENT_DATA,
          payload: {
            status: statusData.status,
            processingStatus: statusData.wConsentStatus,
            errorMessage: statusData.errors,
          },
        });

        if (isConsentComplete(statusData.wConsentStatus)) {
          await updateStatus();
        } else if (isConsentFailed(statusData.status, statusData.wConsentStatus)) {
          dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
        }
      }
    } catch (error) {
      const errorData = error?.response?.data;
      const errorCode = errorData?.detail?.code;
      if (
        errorCode === 'create_new' ||
        errorCode === 'no_consent' ||
        error?.response?.status === 404
      ) {
        dispatch({type: ACTION_TYPES.SET_HAS_EXISTING_CONSENT, payload: false});
      } else {
        handleApiError(error);
      }
    } finally {
      dispatch({type: ACTION_TYPES.SET_LOADING, payload: false});
    }
  }, [updateStatus, dispatch]);

  const handleContinue = useCallback(async () => {
    try {
      dispatch({type: ACTION_TYPES.SET_LOADING, payload: true});
      dispatch({type: ACTION_TYPES.RESET_ERROR});

      const response = await createAAConsent();
      const data = response.data;

      if (data.aaDetails && data.aaDetails.url) {
        window.location.href = data.aaDetails.url;
        // dispatch({type: ACTION_TYPES.SET_VIEW_STATE, payload: VIEW_STATES.POLLING});
        startPolling(data.aaDetails.id);
      } else {
        triggerToast('Unable to generate consent request. Please try again.');
        dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
      }
    } catch (error) {
      handleApiError(error);
      dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
    }
  }, [dispatch, startPolling]);

  const handleSkip = useCallback(async () => {
    try {
      await skipStep(() => {
        window.location.reload();
      });
    } catch (error) {
      handleApiError(error);
      dispatch({type: ACTION_TYPES.SET_SHOW_UPLOAD_OPTION, payload: true});
    }
  }, [skipStep, dispatch]);

  const handleUploadIncome = useCallback(async () => {
    handleSkip();
  }, [handleSkip]);

  return {
    fetchCurrentConsentStatus,
    handleContinue,
    handleSkip,
    handleUploadIncome,
    handleRetry: fetchCurrentConsentStatus,
    isSkipLoading,
  };
};

export default useAAActions;
